// Mock data for Pawaac Krishan Agricultural Drone Landing Page

export const heroData = {
  title: "Pawaac Krishan",
  subtitle: "Making Farming Smarter, Faster & Efficient",
  description: "Autonomous agri-drone designed for precision farming and maximum yield.",
  backgroundImage: "https://ik.imagekit.io/5nbfbjinp/ChatGPT%20Image%20Aug%2022,%202025,%2003_41_30%20AM.png?updatedAt=1755814314708",
  ctaText: "Request a Demo",
  stats: [
    { value: "6", unit: "acres/hrs", label: "Coverage" },
    { value: "22", unit: "mins", label: "Flight Time" },
    { value: "2", unit: "km", label: "Range" }
  ]
};

export const droneHighlights = {
  title: "Advanced Specifications",
  subtitle: "Built for Performance, Designed for Precision",
  specifications: [
    {
      icon: "Layers",
      label: "Multiutility",
      value: "Swappable payloads",
      description: "Versatile payload system for different farming needs"
    },
    {
      icon: "Weight",
      label: "Takeoff Weight",
      value: "29.84 kg",
      description: "Optimal weight for stability and payload capacity"
    },
    {
      icon: "Droplets",
      label: "Spray Tank",
      value: "11 L",
      description: "Large capacity tank for extended operations"
    },
    {
      icon: "Clock",
      label: "Flight Time",
      value: "22 mins",
      description: "Extended flight duration for maximum coverage"
    },
    {
      icon: "Radar",
      label: "Sensors",
      value: "Terrain & obstacle",
      description: "Advanced sensor array for safe autonomous flight"
    },
    {
      icon: "Smartphone",
      label: "Mobile App",
      value: "Agri-specific",
      description: "Dedicated mobile application for farm management"
    },
    {
      icon: "Zap",
      label: "Flying Speed",
      value: "0–15 m/s",
      description: "Variable speed control for different applications"
    },
    {
      icon: "Radio",
      label: "Range",
      value: "2 km",
      description: "Extended control range for large field operations"
    },
    {
      icon: "Droplets",
      label: "Spray Flow",
      value: "0–5 L/min",
      description: "Precision flow control for optimal chemical usage"
    },
    {
      icon: "MapPin",
      label: "Coverage",
      value: "6 acres/hour",
      description: "Maximum efficiency for time-sensitive operations"
    }
  ]
};

export const impactData = {
  title: "Transforming Agriculture",
  subtitle: "Real Impact, Measurable Results",
  benefits: [
    {
      icon: "Clock",
      title: "Save Time",
      stat: "10x Faster",
      description: "Cover 6 acres in just 1 hour compared to traditional methods",
      improvement: "90% time reduction"
    },
    {
      icon: "Droplets",
      title: "Reduce Chemical Usage",
      stat: "30% Less",
      description: "Precision spraying reduces chemical wastage significantly",
      improvement: "Up to 30% reduction"
    },
    {
      icon: "TrendingUp",
      title: "Increase Yield",
      stat: "25% More",
      description: "Targeted treatments lead to healthier crops and better harvests",
      improvement: "Average 25% increase"
    },
    {
      icon: "DollarSign",
      title: "Cut Costs",
      stat: "40% Savings",
      description: "Reduce labor, fuel, and chemical costs with automated precision",
      improvement: "Overall 40% cost reduction"
    }
  ],
  image: "https://images.unsplash.com/photo-1656407410275-e63e689bcd90"
};

export const autonomyData = {
  title: "Autonomy & Intelligence",
  subtitle: "Smart Technology for Smart Farming",
  features: [
    {
      title: "Autonomous Flying",
      description: "Advanced flight control system with GPS precision and obstacle avoidance",
      benefits: ["Pre-programmed flight paths", "Automatic takeoff and landing", "Real-time path adjustment"]
    },
    {
      title: "Field Mapping",
      description: "Create detailed field maps with precision measurement and boundary detection",
      benefits: ["High-resolution field scanning", "Boundary identification", "Crop health analysis"]
    },
    {
      title: "Yield Assessment",
      description: "AI-powered crop monitoring and yield prediction for better farm planning",
      benefits: ["Crop health monitoring", "Disease detection", "Yield forecasting"]
    }
  ],
  appFeatures: {
    title: "Mobile App Control",
    description: "Complete drone management from your smartphone",
    features: [
      "Real-time flight monitoring",
      "Mission planning and execution",
      "Data analytics and reports",
      "Remote diagnostics",
      "Weather integration",
      "Maintenance scheduling"
    ],
    image: "https://ik.imagekit.io/5nbfbjinp/ChatGPT%20Image%20Aug%2022,%202025,%2003_45_27%20AM.png?updatedAt=1755814543178"
  }
};

export const visionModelData = {
  title: "AI Vision Model",
  subtitle: "Next-Level Precision Farming with Computer Vision",
  description: "Our advanced AI vision model revolutionizes agriculture by providing real-time crop analysis, yield prediction, and precision farming insights that maximize productivity and sustainability.",
  features: [
    {
      icon: "Brain",
      title: "Smart Crop Analysis",
      description: "AI-powered computer vision analyzes crop health, growth patterns, and stress indicators in real-time",
      capabilities: [
        "Disease detection with 95% accuracy",
        "Nutrient deficiency identification",
        "Growth stage monitoring",
        "Pest infestation alerts"
      ],
      metrics: {
        accuracy: "95%",
        coverage: "100 acres/hour",
        detection: "Early stage"
      }
    },
    {
      icon: "TrendingUp",
      title: "Yield Prediction",
      description: "Machine learning algorithms predict harvest yields with unprecedented accuracy for better planning",
      capabilities: [
        "Season-long yield forecasting",
        "Weather impact analysis",
        "Crop maturity estimation",
        "Quality grade prediction"
      ],
      metrics: {
        accuracy: "92%",
        timeline: "120 days ahead",
        precision: "Field-level"
      }
    },
    {
      icon: "Target",
      title: "Precision Application",
      description: "Variable rate technology optimizes input application based on real-time field conditions",
      capabilities: [
        "Variable rate spraying",
        "Targeted fertilizer application",
        "Water management optimization",
        "Selective harvesting guidance"
      ],
      metrics: {
        efficiency: "40% improvement",
        waste: "60% reduction",
        coverage: "Centimeter precision"
      }
    }
  ],
  benefits: [
    {
      title: "Increase Productivity",
      value: "35%",
      description: "Higher yields through optimized farming practices"
    },
    {
      title: "Reduce Input Costs",
      value: "45%",
      description: "Precise application reduces waste and saves money"
    },
    {
      title: "Improve Sustainability",
      value: "50%",
      description: "Less chemical usage and environmental impact"
    },
    {
      title: "Save Time",
      value: "60%",
      description: "Automated analysis and decision-making"
    }
  ],
  techSpecs: {
    title: "Technical Specifications",
    specs: [
      { label: "Image Resolution", value: "4K Ultra HD" },
      { label: "Processing Speed", value: "Real-time" },
      { label: "AI Model", value: "Deep Learning CNN" },
      { label: "Training Data", value: "10M+ images" },
      { label: "Accuracy Rate", value: "95%+" },
      { label: "Detection Classes", value: "50+ types" }
    ]
  },
  image: "https://ik.imagekit.io/5nbfbjinp/WhatsApp%20Image%202025-08-22%20at%2001.13.55.jpeg?updatedAt=1755813850020"
};

export const ctaData = {
  title: "Bring Efficiency to Your Farm",
  subtitle: "Experience the Future of Precision Agriculture",
  description: "Join hundreds of progressive farmers who have revolutionized their operations with Pawaac Krishan. See how our autonomous drone technology can transform your farm's productivity and profitability.",
  buttonText: "Request a Demo",
  image: "https://ik.imagekit.io/5nbfbjinp/WhatsApp%20Image%202025-08-22%20at%2001.13.56.jpeg?updatedAt=1755813850133",
  features: [
    "Free consultation and field assessment",
    "Customized demonstration for your crops",
    "ROI analysis and cost-benefit report",
    "Training and ongoing support included"
  ]
};

export const navData = {
  logo: "Pawaac",
  logoImage: "https://customer-assets.emergentagent.com/job_crop-tech-drone/artifacts/6gd1aphi_pawak%20drone%20round%20black%20nd%20white%20svg.svg",
  menuItems: [
    { label: "Specifications", href: "#specifications" },
    { label: "Impact", href: "#impact" },
    { label: "AI Vision", href: "#vision" },
    { label: "Technology", href: "#technology" },
    { label: "Demo", href: "#demo" }
  ]
};

export const footerData = {
  company: "Pawaac",
  description: "Leading agricultural drone technology for precision farming and maximum yield optimization.",
  contact: {
    phone: "+91 734 7711 100",
    email: "info@pawaac.com",
    address: " 9th Main Rd, Jayanagar, Bengaluru, Karnataka 560011"
  },
  links: {
    product: [
      { label: "Krishan Specifications", href: "#" },
      { label: "Features", href: "#" },
      { label: "Pricing", href: "#" }
    ],
    support: [
      { label: "Documentation", href: "#" },
      { label: "Training", href: "#" },
      { label: "Support Center", href: "#" }
    ],
    company: [
      { label: "About Us", href: "#" },
      { label: "Contact", href: "#demo" }
    ]
  },
  social: [
    { platform: "Twitter", href: "#" },
    { platform: "LinkedIn", href: "#" },
    { platform: "YouTube", href: "#" }
  ]
};